/**
 * "Press K" button + pause-on-open command list modal.
 *
 * Sits to the left of the minimap. Click the button or press K to toggle;
 * while open the modal pauses the sim (owner reads `isPaused`).
 */
export class KeyHelp {
  readonly button: HTMLButtonElement;
  readonly modal: HTMLDivElement;
  private _open = false;

  constructor(opts: { minimapSize?: number } = {}) {
    const minimapSize = opts.minimapSize ?? 280;
    const rightOfMinimap = 8 + minimapSize + 8; // margin + width + gap

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Press K — key commands';
    btn.style.cssText = [
      'position:fixed',
      'top:8px',
      `right:${rightOfMinimap}px`,
      'background:rgba(15,20,25,0.82)',
      'color:#eee',
      'border:1px solid rgba(255,255,255,0.1)',
      'border-radius:8px',
      'padding:8px 12px',
      'font:11px/1.2 system-ui, sans-serif',
      'letter-spacing:0.02em',
      'cursor:pointer',
      'pointer-events:auto',
      'z-index:6',
      'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
    ].join(';');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(30,40,50,0.9)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(15,20,25,0.82)';
    });

    const modal = document.createElement('div');
    modal.style.cssText = [
      'position:fixed',
      'inset:0',
      'background:rgba(0,0,0,0.55)',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'z-index:10',
      'pointer-events:auto',
      'font:12px/1.45 system-ui, sans-serif',
      'color:#eee',
    ].join(';');
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.close();
    });

    const card = document.createElement('div');
    card.style.cssText = [
      'background:rgba(20,26,32,0.97)',
      'border-radius:12px',
      'padding:20px 28px',
      'min-width:360px',
      'max-width:520px',
      'box-shadow:0 12px 40px rgba(0,0,0,0.6)',
      'border:1px solid rgba(255,255,255,0.08)',
    ].join(';');

    card.innerHTML = `
      <div style="font-size:14px; letter-spacing:0.08em; text-transform:uppercase; opacity:0.75; margin-bottom:14px;">
        Commands
      </div>
      <div style="display:grid; grid-template-columns:auto 1fr; gap:6px 18px;">
        ${row('Left-click terrain', 'Move your yellow ant there')}
        ${row('Click minimap', 'Set player goal (teleport camera in follow mode)')}
        ${row('Left-drag', 'Orbit camera')}
        ${row('Scroll', 'Zoom')}
        ${row('1', 'Summon 2 followers')}
        ${row('2', 'Summon 5 followers')}
        ${row('3', 'Release half of followers')}
        ${row('4', 'Release all followers')}
        ${row('F', 'Toggle follow-cam on player')}
        ${row('G', 'Fly camera to player')}
        ${row('H', 'Send player home to red nest')}
        ${row('V', 'Toggle first-person ant-cam')}
        ${row('M', 'Toggle magnifying glass (burn)')}
        ${row('B (hold)', 'Bug spray cone at cursor')}
        ${row('K / click outside', 'Close this window')}
      </div>
      <div style="margin-top:16px; opacity:0.55; font-size:11px;">
        Game paused while this is open.
      </div>
    `;
    modal.appendChild(card);

    document.body.appendChild(btn);
    document.body.appendChild(modal);

    this.button = btn;
    this.modal = modal;
  }

  get isPaused(): boolean {
    return this._open;
  }

  toggle(): void {
    if (this._open) this.close();
    else this.open();
  }

  open(): void {
    if (this._open) return;
    this._open = true;
    this.modal.style.display = 'flex';
  }

  close(): void {
    if (!this._open) return;
    this._open = false;
    this.modal.style.display = 'none';
  }
}

function row(keys: string, desc: string): string {
  return `
    <kbd style="background:rgba(255,255,255,0.08); padding:2px 8px; border-radius:4px; border:1px solid rgba(255,255,255,0.14); font:inherit; white-space:nowrap;">${keys}</kbd>
    <span style="opacity:0.85;">${desc}</span>
  `;
}
